import * as React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="font-serif font-medium text-base text-ink mb-2">{title}</h3>
    {children}
  </div>
);

export const TermsContent: React.FC = () => (
  <>
    <p className="text-xs text-ink-500 mb-4">
      <strong className="text-ink">Effective Date:</strong> [PLACEHOLDER — insert date before launch]
      &nbsp;·&nbsp;
      <strong className="text-ink">Last Updated:</strong> [PLACEHOLDER]
    </p>
    <p className="text-xs font-medium uppercase tracking-widest text-oxblood mb-6">
      ⚠ Placeholder — Review with a securities attorney before public launch
    </p>

    <Section title="1. Acceptance of Terms">
      <p>
        By accessing or using Oracle Insight (the "Platform"), you agree to be bound by these Terms
        of Service ("Terms"). If you do not agree to these Terms, you may not access or use the
        Platform. These Terms constitute a legally binding agreement between you and Oracle Insight
        ("we," "us," or "our"). We reserve the right to update these Terms at any time. Continued
        use of the Platform after changes are posted constitutes your acceptance of the revised Terms.
      </p>
    </Section>

    <Section title="2. Description of Service">
      <p>
        Oracle Insight is an AI-powered equity research and analysis platform designed for
        informational and educational purposes. The Platform analyzes publicly available financial
        data and produces research memos, verdicts, screener results, and other analytical outputs.
        All analysis is generated using artificial intelligence and automated data processing. The
        Platform is not a brokerage, investment adviser, financial planner, or registered securities
        professional of any kind.
      </p>
    </Section>

    <Section title="3. Not Investment Advice — Important Disclaimer">
      <p className="font-medium text-ink mb-2">
        Nothing on this Platform constitutes investment advice, financial advice, trading advice, or
        any recommendation to buy, sell, or hold any security or financial instrument.
      </p>
      <p>
        Oracle Insight is not registered as an investment adviser with the U.S. Securities and
        Exchange Commission (SEC) or any state securities authority. Use of this Platform does not
        create an adviser-client relationship of any kind. All outputs — including analysis, memos,
        verdicts, signals, scores, and projections — are provided solely for informational and
        educational purposes. You should always consult a qualified financial professional before
        making any investment decision. Past performance referenced on the Platform is not indicative
        of future results.
      </p>
    </Section>

    <Section title="4. Eligibility and User Accounts">
      <p>
        You must be at least 18 years of age to use the Platform. By registering, you represent
        that the information you provide is accurate and complete. You are responsible for
        maintaining the confidentiality of your account credentials and for all activity that
        occurs under your account. You agree to notify us immediately of any unauthorized use of
        your account. We reserve the right to suspend or terminate accounts that violate these Terms
        or that we determine, in our sole discretion, pose a risk to the Platform or its users.
      </p>
    </Section>

    <Section title="5. Subscription and Payment">
      <p>
        [PLACEHOLDER — Insert subscription tier structure, pricing, billing cycle, refund policy,
        and payment processor terms (Stripe) once finalized. Key points to address: trial periods
        if any; auto-renewal disclosure; cancellation policy; what happens to data on cancellation.]
      </p>
    </Section>

    <Section title="6. Permitted Use and Prohibited Conduct">
      <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Use the Platform's outputs as the sole basis for any investment decision;</li>
        <li>Reproduce, redistribute, or resell any Platform output or data for commercial purposes without written permission;</li>
        <li>Attempt to reverse engineer, scrape, or circumvent any security feature of the Platform;</li>
        <li>Use the Platform to engage in market manipulation, insider trading, or any unlawful activity;</li>
        <li>Share your account credentials with any third party;</li>
        <li>Upload or transmit malicious code, viruses, or any content that interferes with Platform operation.</li>
      </ul>
    </Section>

    <Section title="7. Intellectual Property">
      <p>
        All content, software, design, and technology comprising the Platform are the exclusive
        property of Oracle Insight and its licensors. These Terms do not grant you any license to
        our intellectual property except the limited right to use the Platform for personal,
        non-commercial purposes as expressly permitted herein. The name "Oracle Insight," the
        Platform's logo, and all related marks are proprietary.
      </p>
    </Section>

    <Section title="8. Third-Party Data and Services">
      <p>
        The Platform integrates with third-party data providers (including financial data vendors)
        and AI services. We make no warranty regarding the accuracy, completeness, or timeliness of
        third-party data. Your use of certain features may be subject to the terms of service of
        third-party providers. We are not responsible for any third-party services, content, or
        privacy practices.
      </p>
    </Section>

    <Section title="9. Disclaimer of Warranties">
      <p>
        THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
        EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT
        THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS. AI-GENERATED
        OUTPUTS MAY CONTAIN ERRORS, OMISSIONS, OR OUTDATED INFORMATION.
      </p>
    </Section>

    <Section title="10. Limitation of Liability">
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ORACLE INSIGHT AND ITS OPERATORS,
        EMPLOYEES, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM,
        INCLUDING BUT NOT LIMITED TO INVESTMENT LOSSES, LOSS OF DATA, OR LOSS OF PROFITS, EVEN IF
        WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN NO EVENT SHALL OUR TOTAL
        LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE MONTHS
        PRECEDING THE CLAIM OR (B) ONE HUNDRED DOLLARS ($100).
      </p>
    </Section>

    <Section title="11. Indemnification">
      <p>
        You agree to indemnify, defend, and hold harmless Oracle Insight and its operators from and
        against any claims, liabilities, damages, judgments, losses, costs, or expenses (including
        reasonable attorneys' fees) arising out of or relating to your use of the Platform, your
        violation of these Terms, or your violation of any applicable law or regulation.
      </p>
    </Section>

    <Section title="12. Governing Law and Disputes">
      <p>
        [PLACEHOLDER — Insert governing law (e.g., State of [STATE]) and dispute resolution
        mechanism (e.g., binding arbitration, class action waiver, or litigation). Consult your
        attorney on the appropriate jurisdiction and dispute mechanism for your situation.]
      </p>
    </Section>

    <Section title="13. Changes to These Terms">
      <p>
        We may update these Terms at any time by posting a revised version on the Platform. We will
        make reasonable efforts to notify registered users of material changes. Your continued use
        of the Platform following notice of changes constitutes your acceptance of the updated Terms.
      </p>
    </Section>

    <Section title="14. Contact">
      <p>
        Questions about these Terms may be directed to: [PLACEHOLDER — legal@oracleinsight.ai or
        your designated contact address].
      </p>
    </Section>
  </>
);
